import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Globe, Info, MapPin, Phone } from "lucide-react";

import SurfaceCard from "@/components/primitives/SurfaceCard";
import OfficesCard from "@/components/organization/OfficesCard";
import { NeutralMonogram } from "@/components/explore/exploreShared";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { httpUrl, locationLabel, orgTypeLabel } from "@/lib/publicOrganizationProfile";
import { orgKindNoun } from "@/lib/orgTypeOptions";
import {
  LISTING_SUPPORT_EMAIL,
  claimMailto,
  correctionMailto,
  listedAddressLine,
  listingBannerText,
  orgClaimPath,
  removalMailto,
  telHref,
  type ListedOrgIdentity,
} from "@/lib/listedOrganization";
import type { ListingFacts } from "@/lib/listingFacts";

/**
 * A basic listing (#1040; plan item 11, decisions F2–F4): the page a
 * staff-listed, ownerless organization shows, signed out and signed in alike.
 *
 * It renders identity facts only — name, type, location, website, phone,
 * street address, offices, and a logo only when staff confirmed one (F3);
 * otherwise a monogram. Every story section the brokerage has not completed
 * says "Not shared yet" at section level, never a zero or a blank (F4,
 * product honesty). Benefits, perks and social links are not sections here at
 * all: unselected items stay absent (CLAUDE.md), and a listing has none.
 *
 * Nothing on this page reads a story field. The callers hand it a
 * ListedOrgIdentity (src/lib/listedOrganization.ts), so a narrative value that
 * somehow reached the organization row still could not render here.
 *
 * Listing public facts (founder decision 2026-10-22): staff may approve a
 * small set of facts per listing: an About summary in Compaire's words with
 * its source, service types, markets served and the year founded. They come
 * only from `listing_facts` (src/lib/listingFacts.ts) and are labelled as
 * Compaire's, never as the brokerage's words. Everything else (compensation,
 * fees, leads, benefits, training and support, culture, performance, tools,
 * agent count) stays "Not shared yet".
 */

const STORY_SECTION = {
  title: (name: string) => `About ${name}`,
  body: (noun: string) => `Mission, story and what makes the ${noun} different.`,
};

const NOT_SHARED_SECTIONS: { id: string; title: (name: string) => string; body: string }[] = [
  { id: "value", title: () => "What agents get", body: "Compensation model, fees, training, leads and support." },
  { id: "performance", title: () => "Performance", body: "Production, agent outcomes and market presence." },
  { id: "operations", title: () => "How it runs", body: "Technology, tools and the support agents work with." },
];

/**
 * One "Tell your Story" control, two targets (FEATURE_FLAGS.ORG_CLAIM_REQUESTS): the
 * in-app request page when on, the support-email stopgap when off.
 */
function ClaimLink({ org, className, children }: { org: ListedOrgIdentity; className: string; children: ReactNode }) {
  if (FEATURE_FLAGS.ORG_CLAIM_REQUESTS) {
    return (
      <Link to={orgClaimPath(org.slug)} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={claimMailto(org)} className={className}>
      {children}
    </a>
  );
}

function NotSharedSection({ title, body }: { title: string; body: string }) {
  return (
    <section aria-label={title} data-not-shared>
      <SurfaceCard padding="md" className="rounded-xl">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="m-0 text-[18px] font-semibold leading-[1.25] text-ink-900">{title}</h2>
          <span className="text-[13px] font-medium text-ink-500">Not shared yet</span>
        </div>
        <p className="mt-1.5 text-[14px] leading-[1.6] text-ink-500">{body}</p>
      </SurfaceCard>
    </section>
  );
}

/** Compaire's summary, its source, and an honest note that the brokerage has not told its own story. */
function AboutSection({ name, summary }: { name: string; summary: NonNullable<ListingFacts["summary"]> }) {
  return (
    <section aria-label={`About ${name}`} data-listing-about>
      <SurfaceCard padding="md" className="rounded-xl">
        <h2 className="m-0 text-[18px] font-semibold leading-[1.25] text-ink-900">About {name}</h2>
        <p className="mb-0 mt-2 text-[15px] leading-[1.6] text-ink-900">{summary.text}</p>
        <p className="mb-0 mt-3 text-[13px] leading-[1.5] text-ink-500">
          Prepared by Compaire from public sources. Source:{" "}
          <a
            href={summary.sourceUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1 font-medium text-ink-700 underline-offset-4 hover:underline"
          >
            {summary.sourceHost}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
        <p className="mb-0 mt-1 text-[13px] leading-[1.5] text-ink-500">
          Mission and story: <span className="font-medium">Not shared yet</span> by {name}.
        </p>
      </SurfaceCard>
    </section>
  );
}

/** Service types, markets and year founded, when staff approved any. */
function FactsSection({ facts }: { facts: ListingFacts }) {
  const rows: { label: string; value: ReactNode }[] = [];
  if (facts.foundedYear !== null) rows.push({ label: "Founded", value: String(facts.foundedYear) });
  if (facts.serviceTypes.length > 0) {
    rows.push({
      label: "Service types",
      value: (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {facts.serviceTypes.map((t) => (
            <li key={t} className="rounded-md border border-border px-2 py-0.5 text-[13px] text-ink-700">
              {t}
            </li>
          ))}
        </ul>
      ),
    });
  }
  if (facts.markets.length > 0) rows.push({ label: "Markets served", value: facts.markets.join(", ") });
  if (rows.length === 0) return null;
  return (
    <section aria-label="At a glance" data-listing-facts>
      <SurfaceCard padding="md" className="rounded-xl">
        <h2 className="m-0 mb-3 text-[18px] font-semibold leading-[1.25] text-ink-900">At a glance</h2>
        <dl className="m-0 grid gap-3 sm:grid-cols-2">
          {rows.map((r) => (
            <div key={r.label} className="flex flex-col gap-1">
              <dt className="text-[13px] font-medium text-ink-500">{r.label}</dt>
              <dd className="m-0 text-[15px] text-ink-900">{r.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mb-0 mt-3 text-[13px] leading-[1.5] text-ink-500">Prepared by Compaire from public sources.</p>
      </SurfaceCard>
    </section>
  );
}

export interface ListedOrganizationViewProps {
  org: ListedOrgIdentity;
  /** Staff-approved listing facts (listingFactsFromPublic); null or absent shows none. */
  facts?: ListingFacts | null;
  /**
   * The facts read failed: say so in place of the About and At a glance
   * sections rather than claiming they are "Not shared yet".
   */
  factsUnavailable?: boolean;
  /** The public page's name is its only h1; inside the app shell it is an h2. */
  titleAs?: "h1" | "h2";
  /** Above the banner: the public breadcrumb. */
  topSlot?: ReactNode;
  onBack?: () => void;
}

export default function ListedOrganizationView({
  org,
  facts = null,
  factsUnavailable = false,
  titleAs = "h1",
  topSlot,
  onBack,
}: ListedOrganizationViewProps) {
  const Title = titleAs;
  const kind = orgTypeLabel(org.type);
  const where = locationLabel(org);
  const website = httpUrl(org.website);
  const address = listedAddressLine(org);
  const tel = org.phone ? telHref(org.phone) : null;
  const hasContact = !!(address || org.phone || website);
  // A team is never called a brokerage in the owner invitation.
  const noun = orgKindNoun(org.type);

  return (
    <div className="mx-auto max-w-[1140px] px-4 pb-16 pt-6 sm:px-[30px]" data-listed-organization>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-500 hover:text-ink-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
      )}
      {topSlot}

      <div
        role="note"
        data-listing-banner
        className="mb-4 flex flex-col items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
      >
        <p className="m-0 flex min-w-0 items-start gap-2.5 text-[14px] font-medium leading-[1.5] text-ink-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span data-listing-banner-invite>{listingBannerText(org.name, noun)}</span>
        </p>
        {/* Under the sentence on a phone, aligned with its text; beside it from sm up. */}
        <ClaimLink
          org={org}
          className="ml-[26px] inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-pill border border-primary px-4 py-1.5 text-[13px] font-semibold text-primary hover:bg-primary/10 sm:ml-0"
        >
          Tell your Story
        </ClaimLink>
      </div>

      <header>
        <SurfaceCard padding="lg" className="rounded-xl">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-border bg-card" data-listing-logo>
              {org.logoUrl ? (
                <img src={org.logoUrl} alt={`${org.name} logo`} className="h-full w-full object-contain p-1.5" />
              ) : (
                <NeutralMonogram name={org.name} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <Title className="m-0 text-[30px] font-semibold leading-[1.1] text-ink-900 sm:text-[36px]">{org.name}</Title>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-ink-500">
                {kind && <span>Real estate {kind.toLowerCase()}</span>}
                {where && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {where}
                  </span>
                )}
              </p>
            </div>
          </div>
        </SurfaceCard>
      </header>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          {hasContact && (
            <section aria-label="Contact">
              <SurfaceCard padding="md" className="rounded-xl">
                <h2 className="m-0 mb-3 text-[18px] font-semibold leading-[1.25] text-ink-900">Contact</h2>
                <dl className="m-0 grid gap-3 sm:grid-cols-2">
                  {address && (
                    <div className="flex flex-col gap-0.5">
                      <dt className="text-[13px] font-medium text-ink-500">Address</dt>
                      <dd className="m-0 inline-flex items-start gap-1.5 text-[15px] text-ink-900">
                        <MapPin className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-500" aria-hidden="true" />
                        {address}
                      </dd>
                    </div>
                  )}
                  {org.phone && (
                    <div className="flex flex-col gap-0.5">
                      <dt className="text-[13px] font-medium text-ink-500">Phone</dt>
                      <dd className="m-0 inline-flex items-center gap-1.5 text-[15px] text-ink-900">
                        <Phone className="h-3.5 w-3.5 shrink-0 text-ink-500" aria-hidden="true" />
                        {tel ? (
                          <a href={tel} className="hover:underline">
                            {org.phone}
                          </a>
                        ) : (
                          org.phone
                        )}
                      </dd>
                    </div>
                  )}
                  {website && (
                    <div className="flex flex-col gap-0.5">
                      <dt className="text-[13px] font-medium text-ink-500">Website</dt>
                      <dd className="m-0 inline-flex items-center gap-1.5 text-[15px]">
                        <Globe className="h-3.5 w-3.5 shrink-0 text-ink-500" aria-hidden="true" />
                        <a
                          href={website}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="break-all font-medium text-primary hover:underline"
                        >
                          {website.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "")}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </SurfaceCard>
            </section>
          )}

          {factsUnavailable ? (
            <section aria-label={STORY_SECTION.title(org.name)} data-listing-facts-unavailable>
              <SurfaceCard padding="md" className="rounded-xl">
                <h2 className="m-0 text-[18px] font-semibold leading-[1.25] text-ink-900">{STORY_SECTION.title(org.name)}</h2>
                <p role="alert" className="mb-0 mt-1.5 text-[14px] leading-[1.6] text-ink-700">
                  This listing&apos;s details couldn&apos;t load. Reload the page to try again.
                </p>
              </SurfaceCard>
            </section>
          ) : facts?.summary ? (
            <AboutSection name={org.name} summary={facts.summary} />
          ) : (
            <NotSharedSection title={STORY_SECTION.title(org.name)} body={STORY_SECTION.body(noun)} />
          )}

          {!factsUnavailable && facts && <FactsSection facts={facts} />}

          {NOT_SHARED_SECTIONS.map((s) => (
            <NotSharedSection key={s.id} title={s.title(org.name)} body={s.body} />
          ))}
        </div>

        <aside className="space-y-4">
          <SurfaceCard padding="md" className="rounded-xl" data-listing-claim>
            <h2 className="m-0 text-[18px] font-semibold leading-[1.25] text-ink-900">
              We&apos;ve started your Story. You make it extraordinary.
            </h2>
            <p className="mt-2 text-[14px] leading-[1.6] text-ink-700">
              We gathered what&apos;s public about {org.name} so agents can find you. Add your mission, how you support
              agents and what you offer, in your own words.
            </p>
            <ClaimLink
              org={org}
              className="mt-4 inline-flex items-center justify-center rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Tell your Story
            </ClaimLink>
            <p className="mt-2 text-[13px] leading-[1.5] text-ink-500" data-listing-claim-help>
              {FEATURE_FLAGS.ORG_CLAIM_REQUESTS
                ? `First, confirm you manage this ${noun}. Our support team reviews every request.`
                : `Or email ${LISTING_SUPPORT_EMAIL}. We check that you manage the ${noun}, then send you an invitation to make its Story yours.`}
            </p>
          </SurfaceCard>

          {org.offices.length > 0 && <OfficesCard offices={org.offices} display="cities" />}

          <div className="px-1 text-[13px] leading-[1.6] text-ink-500">
            <p className="m-0">Prepared by Compaire from public sources. Not yet reviewed by {org.name}.</p>
            <p className="m-0 mt-1 flex flex-wrap gap-x-3">
              <a href={removalMailto(org)} className="font-medium text-ink-700 underline-offset-4 hover:underline">
                Request removal
              </a>
              <a href={correctionMailto(org)} className="font-medium text-ink-700 underline-offset-4 hover:underline">
                Report a correction
              </a>
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
